import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { ViewSwitcher } from "@/components/ViewSwitcher";
import { Pagination } from "@/components/Pagination";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, Phone, Loader2, Search, School, ShieldCheck, Package } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", phone: "", role: "teacher", package_id: "" };
const PAGE_SIZE = 20;

const ROLE_LABEL = { teacher: "Öğretmen", institution_admin: "Kurum Yöneticisi" };

function Avatar({ name, size = "h-14 w-14", rounded = "rounded-2xl", text = "text-lg" }) {
  return (
    <div className={`${size} flex flex-shrink-0 items-center justify-center ${rounded} bg-gradient-to-br from-violet-500 to-fuchsia-600 ${text} font-bold text-white`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function RoleBadge({ role }) {
  const isAdmin = role === "institution_admin";
  return (
    <Badge className={`rounded-full ${isAdmin ? "bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100" : "bg-violet-100 text-violet-700 hover:bg-violet-100"}`}>
      <ShieldCheck className="mr-1 h-3 w-3" />{ROLE_LABEL[role] || role}
    </Badge>
  );
}

export default function Teachers() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "institution_admin";
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("card");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [packages, setPackages] = useState([]);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);

  const load = async () => {
    try {
      const [tRes, pRes, sRes] = await Promise.all([
        api.get("/teachers"),
        api.get("/packages/options").catch(() => ({ data: [] })),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      setTeachers(tRes.data);
      setPackages(pRes.data || []);
      setSubscriptionEnabled(!!sRes.data?.subscription_enabled);
    }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teachers;
    return teachers.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q) ||
      (t.phone || "").toLowerCase().includes(q)
    );
  }, [teachers, search]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name || "", email: t.email || "", password: "", phone: t.phone || "", role: t.role, package_id: t.package_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const upd = { name: form.name, phone: form.phone, email: form.email, role: form.role };
        if (form.password) upd.password = form.password;
        if (form.role === "teacher" || editing.role === "teacher") {
          upd.package_id = form.package_id || "";
        }
        await api.put(`/users/${editing.id}`, upd);
        toast.success("Öğretmen güncellendi");
      } else {
        const body = {
          email: form.email, password: form.password, name: form.name,
          role: form.role, phone: form.phone,
        };
        if (form.role === "teacher" && form.package_id) body.package_id = form.package_id;
        await api.post("/users", body);
        toast.success("Öğretmen eklendi");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/users/${deleteTarget.id}`); toast.success("Öğretmen silindi"); setDeleteTarget(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="teachers-page">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Yönetim</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Öğretmenler</h1>
            <p className="mt-2 text-sm text-slate-600">Öğretmen ve kurum yöneticisi hesaplarını yönet.</p>
          </div>
          <Button onClick={openCreate} data-testid="add-teacher-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700 active:scale-[0.98]">
            <Plus className="mr-1.5 h-4 w-4" /> Öğretmen Ekle
          </Button>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="İsim, e-posta veya telefona göre ara..." className="h-11 rounded-xl pl-10" data-testid="teachers-search" />
          </div>
          <ViewSwitcher value={view} onChange={setView} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="teachers-empty">
            <School className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            {teachers.length === 0 ? "Henüz öğretmen yok. “Öğretmen Ekle” ile başla." : "Aramanızla eşleşen öğretmen yok."}
          </Card>
        ) : view === "card" ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {paged.map((t) => (
              <Card key={t.id} data-testid={`teacher-card-${t.id}`}
                className="overflow-hidden rounded-2xl border-slate-200/60 p-0 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.03)] transition-transform hover:-translate-y-1">
                <div className="flex items-center gap-4 border-t-4 border-violet-500 p-5">
                  <Avatar name={t.name} />
                  <div className="min-w-0">
                    <h3 className="truncate font-heading text-lg font-semibold text-slate-900">{t.name}</h3>
                    <div className="mt-1"><RoleBadge role={t.role} /></div>
                  </div>
                </div>
                <div className="space-y-1.5 border-t border-slate-100 px-5 py-4 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-blue-500" />{t.email}</p>
                  <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-violet-500" />{t.phone || "Telefon yok"}</p>
                  {subscriptionEnabled && t.role === "teacher" && (
                    <p className="flex items-center gap-2"><Package className="h-4 w-4 text-fuchsia-500" />{t.package_name || "Paket atanmadı"}</p>
                  )}
                </div>
                <div className="flex gap-2 border-t border-slate-100 px-5 py-3">
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-teacher-${t.id}`} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Düzenle</Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(t)} data-testid={`delete-teacher-${t.id}`} className="rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="mr-1 h-3.5 w-3.5" /> Sil</Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="overflow-x-auto rounded-2xl border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="teachers-list">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Öğretmen</th>
                  <th className="px-5 py-3">Rol</th>
                  <th className="px-5 py-3">E-posta</th>
                  <th className="px-5 py-3">Telefon</th>
                  {subscriptionEnabled && <th className="px-5 py-3">Paket</th>}
                  <th className="px-5 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((t) => (
                  <tr key={t.id} data-testid={`teacher-row-${t.id}`} className="border-b border-slate-50 transition-colors last:border-0 hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={t.name} size="h-9 w-9" rounded="rounded-lg" text="text-sm" />
                        <p className="font-medium text-slate-800">{t.name}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3"><RoleBadge role={t.role} /></td>
                    <td className="px-5 py-3 text-slate-600">{t.email}</td>
                    <td className="px-5 py-3 text-slate-600">{t.phone || "—"}</td>
                    {subscriptionEnabled && (
                      <td className="px-5 py-3 text-slate-600">{t.role === "teacher" ? (t.package_name || "—") : "—"}</td>
                    )}
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-teacher-${t.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(t)} data-testid={`delete-teacher-${t.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {!loading && <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="teacher-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Öğretmen Düzenle" : "Yeni Öğretmen"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2"><Label>Ad Soyad *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="teacher-name-input" /></div>
            <div className="space-y-2"><Label>E-posta *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-email-input" className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{editing ? "Yeni Şifre (opsiyonel)" : "Şifre *"}</Label><Input type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Değiştirmek için doldur" : ""} data-testid="teacher-password-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="teacher-phone-input" /></div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={!canManage}>
                  <SelectTrigger data-testid="teacher-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teacher">Öğretmen</SelectItem>
                    {canManage && <SelectItem value="institution_admin">Kurum Yöneticisi</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(form.role === "teacher" || editing?.role === "teacher") && (
              <div className="space-y-2">
                <Label>Abonelik Paketi {subscriptionEnabled ? "" : "(abonelik pasifken etkisiz)"}</Label>
                <Select value={form.package_id || "__none__"} onValueChange={(v) => setForm({ ...form, package_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="teacher-package-select"><SelectValue placeholder="Paket seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Paket yok</SelectItem>
                    {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="teacher-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-teacher-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Öğretmeni sil?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.name}</strong> hesabı silinecek ve öğrenci bağlantıları kaldırılacak.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-teacher" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
